/**
 * Composition layer: assembles `RewardSnapshot`, `CelebrationCue`, and
 * `RewardsExplainerView` from the lower-level xp/levels/badges/config
 * modules. This is the module `src/data/domain-api/create-domain-api.ts`
 * calls; it owns no policy beyond wiring the pieces together in the shapes
 * `domain-api.ts` fixes.
 *
 * Streak (activated this pass, phase-2b-habits.md §5/AC-B22):
 * `RewardSnapshot.streakDays` is now real -- consecutive local calendar days
 * with at least one active `checkin.completed` event, counting back from
 * `today`. An incomplete "today" (no check-in yet) never breaks the streak
 * retroactively (ADR-0006: "counting back from today... an incomplete today
 * never breaks the streak") -- if today has no check-in, the walk starts
 * from yesterday instead. See `./streak.ts` (`computeStreakDays`) for the
 * shared algorithm, also used by `badges.ts`'s `checkin_streak_weeks`.
 */

import type {
  BadgeDefinition,
  CelebrationCue,
  CompassEvent,
  HabitCategory,
  RewardConfig,
  RewardLevelRowView,
  RewardSnapshot,
  RewardsExplainerView,
  RewardXpSourceView,
} from '../../contracts';
import { STARTER_BADGES } from '../../contracts';
import { badgesNewlyEarned } from './badges';
import { deriveLevel, identityForLevel } from './levels';
import { computeStreakDays } from './streak';
import { computeXpTotal } from './xp';

/**
 * Live XP sources as of this pass (phase-2c-weekly-review.md WR-18/AC-W22):
 * ALL 8 `XpValues` fields are now active, for the first time since the
 * reward engine was designed -- Rewards v1's original two (RW-1/RW-2),
 * phase-2b's three (AC-B7/AC-B11/AC-B14), and this phase's three
 * (`reviewCompleted`/`weeklyTargetBonus`/`adjustmentAccepted`'s
 * review-suggestion path -- WR-15/WR-16/WR-17). No XP source remains
 * dormant; see `xp.ts`'s module doc comment for each source's producing
 * event.
 */
const ACTIVE_XP_SOURCES: ReadonlySet<keyof RewardConfig['xp']> = new Set([
  'actionCompleted',
  'dateAdjustmentAccepted',
  'checkinCompleted',
  'habitCompleted',
  'avoidCleanDay',
  'reviewCompleted',
  'weeklyTargetBonus',
  'adjustmentAccepted',
  // Phase 6 (ADR-0002 addendum 2026-07-07): day.closed/priority.completed
  // are both real, shipped event producers as of this phase (closeDay/
  // completePriority in create-domain-api.ts) -- active from the moment
  // they land, same posture every other source above took at its own
  // shipping phase.
  'dayClosed',
  'priorityCompleted',
]);

/** `XpValues` key order used for both the explainer table and coverage of "no hidden sources". */
const XP_SOURCE_ORDER: readonly (keyof RewardConfig['xp'])[] = [
  'checkinCompleted',
  'habitCompleted',
  'avoidCleanDay',
  'weeklyTargetBonus',
  'reviewCompleted',
  'adjustmentAccepted',
  'actionCompleted',
  'dateAdjustmentAccepted',
  'dayClosed',
  'priorityCompleted',
];

/**
 * `today` is required (not optional) so every caller supplies it explicitly
 * per ADR-0001 rule 2 ("no Date.now() inside src/domain") -- this is a
 * breaking signature change from Rewards v1's two-argument form, made in
 * this pass specifically to compute the real streak (AC-B22); both call
 * sites in `create-domain-api.ts` already have `clock.today()` in scope.
 */
export function computeRewardSnapshot(
  events: readonly CompassEvent[],
  config: RewardConfig,
  today: string,
): RewardSnapshot {
  const xpTotal = computeXpTotal(events, config);
  const { level, xpIntoCurrentLevel, xpRequiredForNextLevel } = deriveLevel(
    xpTotal,
    config.levelCurve,
  );
  return {
    xpTotal,
    level,
    xpIntoCurrentLevel,
    xpRequiredForNextLevel,
    streakDays: computeStreakDays(events, today),
  };
}

export function computeRewardsExplainer(
  config: RewardConfig,
): RewardsExplainerView {
  const xpSources: readonly RewardXpSourceView[] = XP_SOURCE_ORDER.map(
    (source) => ({
      source,
      value: config.xp[source],
      active: ACTIVE_XP_SOURCES.has(source),
    }),
  );

  const levelCount = config.levelCurve.thresholds.length + 1;
  const levels: readonly RewardLevelRowView[] = Array.from(
    { length: levelCount },
    (_, i) => {
      const level = i + 1;
      const identity = identityForLevel(level, config.levelIdentities);
      const totalXpRequired = level === 1 ? 0 : (config.levelCurve.thresholds[level - 2] ?? 0);
      return {
        level,
        name: identity.name,
        iconId: identity.iconId,
        totalXpRequired,
      };
    },
  );

  return { xpSources, levels };
}

/**
 * Build the `CelebrationCue` for one XP-earning command, comparing reward
 * state immediately before vs. after appending `newEvents` to the log.
 * `category` is null unless the caller supplies `options.habitCategory` --
 * objective-side sources (action completion, date-adjustment acceptance)
 * never do, matching "null for non-habit awards" (contract doc comment);
 * habit-side callers (`completeHabit`/`logClean` in create-domain-api.ts)
 * pass the completed habit's category so the celebration can tint itself
 * per-category (PRD §8 "one accent per habit category").
 *
 * `today` is required (badge evaluation needs it for `checkin_streak_weeks`,
 * activated this pass -- see `badges.ts`); every call site already has
 * `clock.today()` in scope.
 */
export function computeCelebrationCue(
  eventsBefore: readonly CompassEvent[],
  newEvents: readonly CompassEvent[],
  config: RewardConfig,
  today: string,
  options?: {
    readonly badges?: readonly BadgeDefinition[];
    readonly habitCategory?: HabitCategory;
  },
): CelebrationCue {
  const eventsAfter = [...eventsBefore, ...newEvents];

  const xpBefore = computeXpTotal(eventsBefore, config);
  const xpAfter = computeXpTotal(eventsAfter, config);
  const xpAwarded = xpAfter - xpBefore;

  const levelBefore = deriveLevel(xpBefore, config.levelCurve).level;
  const levelAfter = deriveLevel(xpAfter, config.levelCurve).level;
  const levelUp = levelAfter > levelBefore ? { newLevel: levelAfter } : null;

  const badges = options?.badges ?? STARTER_BADGES;
  const badgesEarned = badgesNewlyEarned(badges, eventsBefore, eventsAfter, today);

  const category: HabitCategory | null = options?.habitCategory ?? null;

  return { xpAwarded, category, levelUp, badgesEarned };
}
